import { useEffect, useState } from "react";
import {
  FaEdit, FaTrash, FaThermometerHalf, FaEye, FaBox, FaMapMarkerAlt, FaDownload, FaSearch
} from "react-icons/fa";
import {
  getConteneurs,
  createConteneur,
  updateConteneur,
  deleteConteneur,
  getSuiviConteneurs,
  createSuiviConteneur,
  getHistoriqueConteneur,
} from "../../services/conteneurs";
import { getAllEntrepots } from "../../services/entrepots";
import type { Conteneur, ConteneurPayload } from "../../types/conteneur";
import type { Entrepot } from "../../types/entrepot";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import logo from "../../assets/congelcam-logo.png";
import "./Conteneurs.css";

interface LigneReleve {
  id: string;
  date: string;
  type: "releve" | "etat";
  temperature: number | string;
  etat: "R" | "X" | "A" | "S";
  commentaire?: string | null;
}

export default function Conteneurs() {
  const [conteneurs, setConteneurs] = useState<Conteneur[]>([]);
  const [entrepots, setEntrepots] = useState<Entrepot[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedConteneur, setSelectedConteneur] = useState<Conteneur | null>(null);
  const [showComment, setShowComment] = useState(false);

  const [showReleveModal, setShowReleveModal] = useState(false);
  const [showAddReleveModal, setShowAddReleveModal] = useState(false);
  const [mergedData, setMergedData] = useState<LigneReleve[]>([]);
  const [conteneurForReleve, setConteneurForReleve] = useState<Conteneur | null>(null);

  // ✅ Ajout recherche
  const [searchEntrepot, setSearchEntrepot] = useState("");
  const [searchReference, setSearchReference] = useState("");

  const filteredConteneurs = conteneurs.filter((c) =>
    c.entrepot.nom.toLowerCase().includes(searchEntrepot.toLowerCase()) &&
    c.reference.toLowerCase().includes(searchReference.toLowerCase())
  );

  const fetchConteneurs = async () => {
    const data = await getConteneurs();
    if (Array.isArray(data)) setConteneurs(data as Conteneur[]);
    else setConteneurs([]);
  };

  useEffect(() => {
    const fetchData = async () => {
      await fetchConteneurs();
      const dataEntrepots = await getAllEntrepots();
      setEntrepots(dataEntrepots);
    };
    fetchData();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const payload: ConteneurPayload = {
      reference: formData.get("reference") as string,
      capacite: Number(formData.get("capacite")),
      description: formData.get("description") as string,
      entrepot_id: formData.get("entrepot_id") as string,
      etat: formData.get("etat") as "A" | "R" | "S" | "X",
      date_arrivee: formData.get("date_arrivee") as string,
      temperature_arrivee: formData.get("temperature_arrivee")
        ? Number(formData.get("temperature_arrivee"))
        : undefined,
      commentaire: formData.get("commentaire") as string || undefined,
    };

    if (selectedConteneur) {
      await updateConteneur(selectedConteneur.id, payload);
    } else {
      await createConteneur(payload);
    }

    await fetchConteneurs();
    setShowModal(false);
    setSelectedConteneur(null);
    setShowComment(false);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Supprimer ce conteneur ?")) {
      await deleteConteneur(id);
      setConteneurs(conteneurs.filter((c) => c.id !== id));
    }
  };

  const handleViewReleves = async (conteneur: Conteneur) => {
    const dataReleves = await getSuiviConteneurs();
    const dataHistoriques = await getHistoriqueConteneur(conteneur.id);

    const relevesForConteneur = dataReleves.filter((r) => r.conteneur.id === conteneur.id);

    const merged: LigneReleve[] = [
      ...relevesForConteneur.map((r) => ({
        type: "releve" as const,
        id: r.id,
        date: r.date,
        temperature: r.temperature,
        etat: "S" as const,
        commentaire: null,
      })),
      ...dataHistoriques.map((h) => ({
        type: "etat" as const,
        id: h.id,
        date: h.date_changement,
        temperature: h.etat,
        etat: h.etat,
        commentaire: h.etat === "S" ? null : h.commentaire,
      })),
    ].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    setMergedData(merged);
    setConteneurForReleve(conteneur);
    setShowReleveModal(true);
  };

  const handleAddReleve = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!conteneurForReleve) return;

    const formData = new FormData(e.currentTarget);
    const payload = {
      conteneur_id: conteneurForReleve.id,
      date: formData.get("date") as string,
      temperature: Number(formData.get("temperature")),
    };

    await createSuiviConteneur(payload);
    await handleViewReleves(conteneurForReleve);
    setShowAddReleveModal(false);
  };

  const exportPDF = () => {
    if (!conteneurForReleve) return;
    const doc = new jsPDF();

    const img = new Image();
    img.src = logo;
    img.onload = () => {
      const imgWidth = img.width;
      const imgHeight = img.height;
      const targetWidth = 40;
      const targetHeight = (imgHeight / imgWidth) * targetWidth;
      const pageWidth = doc.internal.pageSize.getWidth();
      const x = (pageWidth - targetWidth) / 2;

      doc.addImage(img, "PNG", x, 10, targetWidth, targetHeight);

      // ✅ Titre avec entrepôt
      doc.setFontSize(14);
      doc.text(
        `Entrepôt ${conteneurForReleve.entrepot.nom} : Relèves du conteneur ${conteneurForReleve.reference}`,
        pageWidth / 2,
        targetHeight + 25,
        { align: "center" }
      );

      autoTable(doc, {
        startY: targetHeight + 35,
        head: [["Date", "Température / État", "Commentaire"]],
        body: mergedData.map((item) => [
          item.date,
          item.type === "releve" ? `${item.temperature} °C` : item.etat,
          item.commentaire ?? "",
        ]),
        styles: { halign: "center", valign: "middle" },
        headStyles: { fillColor: [37, 99, 235] },
      });

      // ✅ Nom du fichier avec entrepôt
      doc.save(`releves_${conteneurForReleve.entrepot.nom}_${conteneurForReleve.reference}.pdf`);
    };
  };

  return (
    <div className="conteneurs">
      <div className="conteneurs-header">
        {/* ✅ Barres de recherche */}
        <input
          type="text"
          placeholder="Rechercher par entrepôt"
          value={searchEntrepot}
          onChange={(e) => setSearchEntrepot(e.target.value)}
        />
        <input
          type="text"
          placeholder="Rechercher par référence"
          value={searchReference}
          onChange={(e) => setSearchReference(e.target.value)}
        />
        <button className="btn-search">
          <FaSearch />
        </button>
        <button className="btn-ajout" onClick={() => setShowModal(true)}>
          ➕ Ajouter conteneur
        </button>
      </div>

      <div className="conteneurs-grid">
  {filteredConteneurs.map((c) => (
    <div key={c.id} className="conteneur-card">
      <div className="card-header">
        <h3 className="badge-conteneur">
          <FaBox className="icon-badge" /> {c.reference}
        </h3>
        <span
          className={`status-badge ${
            c.etat === "S"
              ? "status-service"
              : c.etat === "R"
              ? "status-retire"
              : c.etat === "X"
              ? "status-vide"
              : "status-defaut"
          }`}
        >
          {c.etat === "S"
            ? "En service"
            : c.etat === "R"
            ? "Retiré"
            : c.etat === "X"
            ? "Vide"
            : "Défaut"}
        </span>
      </div>

      <p className="card-line">
        <FaMapMarkerAlt className="icon-line" /> {c.entrepot.nom}
      </p>
      <p className="card-line">
        <strong>Capacité :</strong> {c.capacite ?? "--"} tonnes
      </p>
      <p className="card-line">
        <strong>Date arrivée :</strong> {c.date_arrivee ?? "--"}
      </p>
      <p className="card-line">
        <strong>Température arrivée :</strong>{" "}
        {c.temperature_arrivee ?? "--"} ºC
      </p>

      <div className="card-actions">
        <FaEye className="icon-view" onClick={() => handleViewReleves(c)} />
        <FaThermometerHalf
          className={`icon-add ${c.etat !== "S" ? "disabled" : ""}`}
          onClick={() => {
            if (c.etat === "S") {
              setConteneurForReleve(c);
              setShowAddReleveModal(true);
            }
          }}
        />
        {c.etat !== "R" && (
          <FaEdit
            className="icon-edit"
            onClick={() => {
              setSelectedConteneur(c);
              setShowModal(true);
              setShowComment(c.etat !== "S");
            }}
          />
        )}
        <FaTrash className="icon-trash" onClick={() => handleDelete(c.id)} />
      </div>
    </div>
  ))}
</div>

{/* Modale CRUD Conteneur */}
{showModal && (
  <div className="modal-overlay">
    <div className="modal">
      <h3>{selectedConteneur ? "Modifier conteneur" : "Ajouter conteneur"}</h3>
      <form onSubmit={handleSubmit}>
        <input
          type="text"
          name="reference"
          placeholder="Référence"
          defaultValue={selectedConteneur?.reference}
          required
        />
        <input
          type="number"
          name="capacite"
          placeholder="Capacité"
          defaultValue={selectedConteneur?.capacite}
        />
        <input
          type="text"
          name="description"
          placeholder="Description"
          defaultValue={selectedConteneur?.description || ""}
        />

        <input
          type="date"
          name="date_arrivee"
          defaultValue={selectedConteneur?.date_arrivee || ""}
        />
        <input
          type="number"
          name="temperature_arrivee"
          placeholder="Température d'arrivée (C)"
          defaultValue={
            selectedConteneur?.temperature_arrivee !== undefined
              ? selectedConteneur.temperature_arrivee.toString()
              : ""
          }
        />

        <select
          name="entrepot_id"
          defaultValue={selectedConteneur?.entrepot.id || ""}
          required
        >
          <option value=""> -- Choisir entrepôt -- </option>
          {entrepots.map((e) => (
            <option key={e.id} value={e.id}>
              {e.nom}
            </option>
          ))}
        </select>

        <select
          name="etat"
          defaultValue={selectedConteneur?.etat || "S"}
          onChange={(e) => setShowComment(e.target.value !== "S")}
        >
          <option value="S">En service</option>
          <option value="R">Retiré</option>
          <option value="X">Vide</option>
          <option value="A">Défaut</option>
        </select>

        {showComment && (
          <textarea
            name="commentaire"
            placeholder="Commentaire (obligatoire si hors service)"
            defaultValue={selectedConteneur?.commentaire || ""}
            required
          />
        )}

        <div className="modal-actions">
          <button type="submit">Enregistrer</button>
          <button type="button" onClick={() => setShowModal(false)}>
            Annuler
          </button>
        </div>
      </form>
    </div>
  </div>
)}

{/* Modale Voir Relevés */}
{showReleveModal && (
  <div className="modal-overlay">
    <div className="modal">
      <h3>
        Entrepôt {conteneurForReleve?.entrepot.nom} : Relevés de{" "}
        {conteneurForReleve?.reference}
      </h3>
      <FaDownload className="icon-download" onClick={exportPDF} />
      <table id="releves-table" className="releves-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Température / État</th>
            <th>Commentaire</th>
          </tr>
        </thead>
        <tbody>
          {mergedData.map((item) => (
            <tr
              key={item.id}
              className={
                item.etat === "S"
                  ? "row-service"
                  : item.etat === "R"
                  ? "row-retire"
                  : item.etat === "X"
                  ? "row-vide"
                  : "row-defaut"
              }
            >
              <td>{item.date}</td>
              <td>
                {item.type === "releve"
                  ? `${item.temperature} ºC`
                  : item.etat}
              </td>
              <td>{item.commentaire ?? ""}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="modal-actions">
        <button type="button" onClick={() => setShowReleveModal(false)}>
          Fermer
        </button>
      </div>
    </div>
  </div>
)}

{/* Modale Ajouter Relevé */}
{showAddReleveModal && (
  <div className="modal-overlay">
    <div className="modal">
      <h3>Ajouter relevé pour {conteneurForReleve?.reference}</h3>
      <form onSubmit={handleAddReleve}>
        <input type="date" name="date" required />
        <input
          type="number"
          name="temperature"
          placeholder="Température (C)"
          required
        />
        <div className="modal-actions">
          <button type="submit">Enregistrer</button>
          <button type="button" onClick={() => setShowAddReleveModal(false)}>
            Annuler
          </button>
        </div>
      </form>
    </div>
  </div>
)}
</div>
);
}
